import { useLocation, useNavigate } from "react-router-dom";

import { useFunnelIntakeScreens } from "../hooks";
import type { FunnelPhase } from "../models";
import { FUNNEL_PHASE } from "../models";
import { FUNNEL_ROUTE, FUNNEL_STEPS } from "../router/constants";
import { useFunnelStore } from "../stores";
import { lastIntakeRoute, previousIntakeRoute } from "../utils/intake-screens";
import { FunnelBrand, FunnelCoverageBadges } from "./funnel-brand";
import { FunnelStepper } from "./funnel-stepper";

const BackIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="size-5 text-neutral-700"
    aria-hidden="true"
  >
    <path d="m15 18-6-6 6-6" />
  </svg>
);

/**
 * Sticky funnel header: levelsRx wordmark + coverage badges on top, then a row with
 * a circular back button and the five-phase stepper. The active phase is derived
 * from the current pathname.
 */
export const FunnelHeader = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const answers = useFunnelStore((store) => store.intakeAnswers) ?? {};
  const {
    query: { data },
    questionsByKey,
  } = useFunnelIntakeScreens();
  const intakeSteps = data?.steps ?? [];

  const intakeSlug = pathname.startsWith(`${FUNNEL_ROUTE.INTAKE}/`)
    ? pathname.slice(FUNNEL_ROUTE.INTAKE.length + 1)
    : undefined;
  const intakeStep = intakeSteps.find((step) => step.slug === intakeSlug);

  const currentIndex = FUNNEL_STEPS.findIndex((step) =>
    pathname.startsWith(step.route),
  );
  const currentPhase: FunnelPhase =
    intakeStep?.phase ??
    FUNNEL_STEPS[Math.max(currentIndex, 0)]?.phase ??
    FUNNEL_PHASE.START;

  const handleBack = () => {
    if (intakeSlug !== undefined) {
      navigate(
        previousIntakeRoute(intakeSteps, intakeSlug, answers, questionsByKey),
      );
      return;
    }

    if (
      pathname.startsWith(FUNNEL_ROUTE.MEDICAL_REVIEW) &&
      intakeSteps.length > 0
    ) {
      navigate(lastIntakeRoute(intakeSteps, answers, questionsByKey));
      return;
    }

    const previousStep = FUNNEL_STEPS[currentIndex - 1];
    if (previousStep) {
      navigate(previousStep.route);
    } else {
      navigate(-1);
    }
  };

  return (
    <header className="w-full border-b border-secondary-100 bg-white">
      <div className="mx-auto flex max-w-4xl items-center justify-center gap-6 px-5 py-3">
        <FunnelBrand />
        <FunnelCoverageBadges />
      </div>

      <div className="mx-auto flex max-w-4xl items-center gap-4 px-5 pb-4">
        <button
          type="button"
          onClick={handleBack}
          aria-label="Back"
          className="flex size-11 shrink-0 items-center justify-center rounded-full bg-white shadow-sm transition hover:bg-secondary-50"
        >
          <BackIcon />
        </button>
        <div className="overflow-x-auto">
          <FunnelStepper currentPhase={currentPhase} />
        </div>
      </div>
    </header>
  );
};
